<script setup lang="ts" generic="TData, TValue">
import type { ColumnDef } from '@tanstack/vue-table'
import {
  FlexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useVueTable,
} from '@tanstack/vue-table'
import { LoaderCircle } from 'lucide-vue-next'

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select'

import { Pagination, PaginationEllipsis, PaginationList, PaginationListItem, PaginationNext, PaginationPrev } from '@/components/ui/pagination'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

const props = defineProps<{
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  page?: number
  page_size?: number
  total?: number
  isLoading?: boolean
}>()

const table = useVueTable({
  get data() { return props.data },
  get columns() { return props.columns },
  getCoreRowModel: getCoreRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
})
const emit = defineEmits(['update:page', 'update:page_size'])
const handlePageChange = (page: number) => {
    emit('update:page', page)
}
const handlePageSizeChange = (page_size: number) => {
    emit('update:page_size', page_size)
}
</script>

<template>
  <div class="border rounded-md relative">
    <div v-if="props.isLoading" class="flex rounded-md justify-center items-center h-full w-full absolute bg-[rgba(255,255,255,0.9)] z-10">
      <LoaderCircle class="w-8 h-8 animate-spin text-blue-500" />
    </div>
    <Table>
      <TableHeader>
        <TableRow v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
          <TableHead v-for="header in headerGroup.headers" :key="header.id">
            <FlexRender
              v-if="!header.isPlaceholder" :render="header.column.columnDef.header"
              :props="header.getContext()"
            />
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <template v-if="table.getRowModel().rows?.length">
          <TableRow
            v-for="row in table.getRowModel().rows" :key="row.id"
            :data-state="row.getIsSelected() ? 'selected' : undefined"
          >
            <TableCell v-for="cell in row.getVisibleCells()" :key="cell.id">
              <FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
            </TableCell>
          </TableRow>
        </template>
        <template v-else>
          <TableRow>
            <TableCell :colspan="columns.length" class="h-24 text-center">
              Пусто
            </TableCell>
          </TableRow>
        </template>
      </TableBody>
    </Table>
    <div class="w-full">
        <Pagination v-slot="{ page }" :items-per-page="props.page_size" :total="props.total" :sibling-count="2" :default-page="props.page" show-edges @update:page="handlePageChange">
            <PaginationList v-slot="{ items }" class="flex items-center gap-1 justify-between w-full p-4">
                <div class="flex gap-2">
                  <Select v-model="props.page_size" @update:modelValue="handlePageSizeChange">
                    <SelectTrigger class="w-auto h-auto bg-white">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectGroup>
                            <SelectItem :value="20">20</SelectItem>
                            <SelectItem :value="50">50</SelectItem>
                            <SelectItem :value="100">100</SelectItem>
                        </SelectGroup>
                    </SelectContent>
                </Select>
                    <template v-for="(item, index) in items">
                        <PaginationListItem v-if="item.type === 'page'" :key="index" :value="item.value" as-child>
                            <Button :class="cn('w-[40px] h-[40px] p-0', item.value === page ? 'border-primary' : '')" variant="outline">
                            {{ item.value }}
                        </Button>
                        </PaginationListItem>
                        <PaginationEllipsis class="text-grey"  v-else :key="item.type" :index="index" />
                    </template>
                </div>
                <div class="flex gap-2">
                    <PaginationPrev />
                    <PaginationNext />
                </div>
            </PaginationList>
        </Pagination>
      </div>
  </div>
</template>